class AIAnalysisAPI {
  constructor() {
    this.apiUrl = 'https://chatkb.gausscode.com/api/v1/chat/completions'
    this.apiKey = 'fastgpt-ogUONHvmVWKS9ypmok4BLfE4rr4pHU86XWlYbDZjyBHiB9HkgGyhT6IYT44F'
    this.appId = '68f9fa4f4cb03574d9069fd3'
  }

  generateChatId() {
    return 'chat_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }

  async analyzeData(prompt, onChunk = null) {
    const requestBody = {
      chatId: this.generateChatId(),
      stream: true,
      detail: false,
      messages: [
        {
          role: 'user',
          content: prompt
        }
      ]
    }

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
          , 'x-app-id': this.appId
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let fullText = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n').filter(line => line.trim() !== '')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') continue

            try {
              const json = JSON.parse(data)
              if (json.choices && json.choices[0] && json.choices[0].delta) {
                const content = json.choices[0].delta.content || ''
                fullText += content
                if (onChunk && content) {
                  onChunk(content, fullText)
                }
              }
            } catch (e) {
              console.warn('解析SSE数据失败:', e)
            }
          }
        }
      }

      return fullText
    } catch (error) {
      console.error('AI分析请求失败:', error)
      throw error
    }
  }

  // 生成首页数据分析提示词（增强版，包含背景/筛选/指标释义与完整JSON数据、证据，并融入用户角色）
  generateDashboardPrompt(stats, sentimentDist, wordCloud, topicSentiment, filters = {}, userRole = '普通用户', userRoleValue = 'normal_user', evidence = null) {
    const indicatorDefinitions = {
      total_count: '总评论数：用户发表的评论总量，反映关注度与样本量',
      positive_count: '正面评论：用户满意/肯定的评价',
      negative_count: '负面评论：用户不满意/抱怨的评价',
      neutral_count: '中性评论：客观描述或无明显情绪倾向',
      avg_score: '平均情感分：数值范围0-100，越高越正面，50为中性',
      sentiment_distribution: '情感分布：正/负/中性在总体中的占比',
      word_cloud: '媒体第一印象词云：关键词及其提及频次',
      topic_sentiment: '话题情绪：按方面(如空间/油耗)的情绪柱状分布'
    }

    // 处理stats数据，将avg_score转换为百分比（如果是0-1之间的小数）
    const processedStats = { ...stats }
    if (processedStats.avg_score && processedStats.avg_score <= 1) {
      processedStats.avg_score = (parseFloat(processedStats.avg_score) * 100).toFixed(2)
    } else if (processedStats.avg_score) {
      processedStats.avg_score = parseFloat(processedStats.avg_score).toFixed(2)
    }

    // 角色描述与视角
    const roleDescriptions = {
      normal_user: '关注产品使用体验和口碑评价',
      product_manager: '关注产品功能与用户体验',
      marketing: '关注品牌形象与市场反馈',
      data_analyst: '关注数据洞察与趋势分析',
      developer: '关注技术实现与性能优化',
      executive: '关注整体业务与战略决策'
    }
    const roleDescription = roleDescriptions[userRoleValue] || roleDescriptions.normal_user
    const personaGuide = {
      normal_user: '用通俗语言，聚焦真实使用体验与建议',
      product_manager: '强调改进点、优先级与体验优化建议',
      marketing: '强调用户感知、传播卖点与口碑机会',
      data_analyst: '突出趋势、显著性与数据支撑',
      developer: '聚焦性能、稳定性与技术优化',
      executive: '简洁结论，强调业务影响与决策要点'
    }[userRoleValue] || '用通俗语言，聚焦真实使用体验与建议'

    const context = {
      page: 'dashboard',
      background: '智能社媒口碑聆听，数据来源于懂车帝用户真实口碑评论',
      timestamp: new Date().toISOString(),
      filters,
      user: { role: userRole, roleValue: userRoleValue, roleDescription },
      indicatorDefinitions,
      data: {
        stats: processedStats,
        sentimentDistribution: sentimentDist,
        wordCloud,
        topicSentiment
      },
      evidence: evidence || {}
    }
    
    return `你现在以“${userRole}”视角（诉求：${roleDescription}）进行口碑解读，请保持“${personaGuide}”。基于下方JSON数据，给出更具洞察力的分析，不要模板化：\n\n${JSON.stringify(context)}\n\n分析线索（供你参考）：\n- 趋势：比较最近30天与前30天的正面率/平均分变化（evidence.trend）\n- 证据：在关键结论处引用用户原话（来自 evidence.quotesByAspect，用『』标注）\n- 伴随关系：指出至少一组“方面共现”的可能因果链（evidence.cooccurrence）\n\n输出要求（250-350字）：\n1) 先用一句话给出整体判断与走向（提升/下滑/稳定）；\n2) 选取1个核心矛盾与1个核心优势，解释“为何出现”（因果-证据-影响）；\n3) 给出一条可执行建议：明确“谁/何时/做什么/影响哪个指标”，贴合${userRole}诉求，并以『原话』收尾增强说服力。`
  }

  // 生成对比页分析提示词（增强版，包含指标释义与完整JSON数据，并融入用户角色）
  generateComparisonPrompt(leftName, rightName, leftStats, rightStats, leftAdvantages, rightAdvantages, leftDisadvantages, rightDisadvantages, userRole = '普通用户', userRoleValue = 'normal_user') {
    const indicatorDefinitions = {
      total_count: '总评论数：关注度与样本量',
      positive_count: '正面评论：满意评价',
      negative_count: '负面评论：不满意评价',
      neutral_count: '中性评论：客观描述',
      avg_score: '平均情感分：数值范围0-100，越高越正面，50为中性',
      advantages: '优势：该车型最常被正面提及的方面',
      disadvantages: '劣势：该车型最常被负面提及的方面'
    }

    // 处理stats数据，将avg_score转换为百分比（如果是0-1之间的小数）
    const processedLeftStats = { ...leftStats }
    const processedRightStats = { ...rightStats }
    
    if (processedLeftStats.avg_score && processedLeftStats.avg_score <= 1) {
      processedLeftStats.avg_score = (parseFloat(processedLeftStats.avg_score) * 100).toFixed(2)
    } else if (processedLeftStats.avg_score) {
      processedLeftStats.avg_score = parseFloat(processedLeftStats.avg_score).toFixed(2)
    }
    
    if (processedRightStats.avg_score && processedRightStats.avg_score <= 1) {
      processedRightStats.avg_score = (parseFloat(processedRightStats.avg_score) * 100).toFixed(2)
    } else if (processedRightStats.avg_score) {
      processedRightStats.avg_score = parseFloat(processedRightStats.avg_score).toFixed(2)
    }

    // 角色描述与风格
    const roleDescriptions = {
      normal_user: '关注产品使用体验和口碑评价',
      product_manager: '关注产品功能与用户体验',
      marketing: '关注品牌形象与市场反馈',
      data_analyst: '关注数据洞察与趋势分析',
      developer: '关注技术实现与性能优化',
      executive: '关注整体业务与战略决策'
    }
    const roleDescription = roleDescriptions[userRoleValue] || roleDescriptions.normal_user
    const personaGuide = {
      normal_user: '通俗易懂，直给结论与建议',
      product_manager: '强调差异化体验与改进优先级',
      marketing: '突出传播点、用户感知与市场机会',
      data_analyst: '用数据支撑差异与趋势',
      developer: '聚焦性能与技术差异',
      executive: '简洁结论与决策要点'
    }[userRoleValue] || '通俗易懂，直给结论与建议'

    const context = {
      page: 'comparison',
      background: '智能社媒口碑聆听，数据来源于懂车帝用户真实口碑评论',
      timestamp: new Date().toISOString(),
      user: { role: userRole, roleValue: userRoleValue, roleDescription },
      models: {
        left: {
          name: leftName,
          stats: processedLeftStats,
          advantages: leftAdvantages,
          disadvantages: leftDisadvantages
        },
        right: {
          name: rightName,
          stats: processedRightStats,
          advantages: rightAdvantages,
          disadvantages: rightDisadvantages
        }
      },
      indicatorDefinitions
    }
    
    return `你现在以“${userRole}”视角（诉求：${roleDescription}，风格：${personaGuide}）对两款车型进行口碑对比。基于下方JSON数据，用约250字输出中文洞察，避免堆砌数据：\n\n${JSON.stringify(context)}\n\n输出要求：\n1) 明确口碑胜负判断及理由（一句话开头）；\n2) 指出最突出的差异点（1-2条，结合优势/劣势与平均分）；\n3) 给出一句话购买建议与推荐场景（贴合${userRole}的关注点）。`
  }

  // 生成优化Markdown提示词（包含角色/目标/策略池/预测与上下文数据）
  generateOptimizationMarkdownPrompt(context = {}) {
    const safeCtx = { ...(context || {}) }
    const processed = {
      ...safeCtx,
      aggregateStats: safeCtx.aggregateStats || {},
      sentimentDistribution: safeCtx.sentimentDistribution || [],
      wordCloud: safeCtx.wordCloud || [],
      topicSentiment: safeCtx.topicSentiment || [],
      compAggregateStats: safeCtx.compAggregateStats || {},
      compTopicSentiment: safeCtx.compTopicSentiment || [],
      strategyPool: (safeCtx.strategyPool || []).slice(0, 20),
      predictions: (safeCtx.predictions || []).slice(0, 20),
      selectedAspects: safeCtx.selectedAspects || [],
      // 卡片数据与角色诉求
      aspectInsights: safeCtx.aspectInsights || {},
      aspectMetrics: safeCtx.aspectMetrics || {},
      roleDescription: safeCtx.roleDescription || ''
    }

    // 辅助：安全格式化与取样
    const pct = (v, d = 1) => {
      const n = parseFloat(v)
      if (!Number.isFinite(n)) return '-'
      return `${(n * 100).toFixed(d)}%`
    }
    const getTopWords = (list = [], k = 3) => {
      return (list || []).slice(0, k).map(w => `『${w.keyword || w.term || ''}』`).filter(Boolean).join('、')
    }
    const getExampleText = (aspect) => {
      const ex = (processed.aspectInsights && processed.aspectInsights[aspect] && processed.aspectInsights[aspect].examples)
        ? processed.aspectInsights[aspect].examples : null
      const neg = ex && ex.negative && ex.negative[0] && ex.negative[0].text
      const pos = ex && ex.positive && ex.positive[0] && ex.positive[0].text
      return (neg || pos || '具体用户反馈示例')
    }
    const getActionForAspect = (aspect) => {
      if (/内饰|座椅|做工|异响/.test(aspect)) return '升级材料与装配工艺，专项治理异响与触感品质'
      if (/车机|智能|系统|互联|卡顿/.test(aspect)) return '优化车机性能与交互，升级软件版本与语音体验'
      if (/动力|加速|变速箱/.test(aspect)) return '调校动力响应曲线，优化换挡逻辑与低速平顺性'
      if (/油耗|能耗|续航/.test(aspect)) return '优化能量管理与节能策略，引入经济模式与技术迭代'
      if (/噪音|NVH|胎噪/.test(aspect)) return 'NVH专项提升：加强隔音材料与底盘优化'
      if (/空间|储物|舒适性/.test(aspect)) return '改善座椅与空间布局，增加储物与舒适配置'
      if (/安全|制动|刹车/.test(aspect)) return '升级安全相关部件与策略，完善主动/被动安全配置'
      return '开展专项根因分析，实施快速试点迭代并验证效果'
    }
    const getRiskForAspect = (aspect) => {
      if (/内饰|座椅/.test(aspect)) return '成本上升与供应链更替风险，需小批量验证'
      if (/车机|智能/.test(aspect)) return '软件回归风险与兼容性问题，需灰度发布与监控'
      if (/动力|变速箱/.test(aspect)) return '调校引发体验分化，需分场景AB测试与用户回访'
      if (/油耗|续航/.test(aspect)) return '节能策略影响性能体验，需在不同驾驶场景平衡'
      if (/噪音|NVH/.test(aspect)) return '结构性改动周期长，需分部件分阶段推进'
      return '投入与产出不确定性，需设立里程碑与退出条件'
    }

    // 关键指标聚合
    const total = processed.aggregateStats.total_count || 0
    const avgScore = processed.aggregateStats.avg_score || 0
    const posCount = processed.aggregateStats.positive_count || 0
    const negCount = processed.aggregateStats.negative_count || 0
    const neuCount = processed.aggregateStats.neutral_count || 0
    const posRatio = total > 0 ? (posCount / total) : 0
    const negRatio = total > 0 ? (negCount / total) : 0
    const neuRatio = total > 0 ? (neuCount / total) : 0

    // 优先级矩阵
    const topPool = (processed.strategyPool || []).slice(0, 5)
    const matrixRows = topPool.map(item => `| ${item.aspect} | ${pct(item.coverage)} | ${pct(item.negRate)} | ${pct(item.absorbPotential || 0)} | **${(parseFloat(item.priorityScore || 0) * 100).toFixed(1)}** |`).join('\n')

    const top1 = topPool[0] || { aspect: '暂无', negRate: 0, coverage: 0, absorbPotential: 0 }
    const m1 = (processed.aspectMetrics && processed.aspectMetrics[top1.aspect]) ? processed.aspectMetrics[top1.aspect] : null
    const top1Neg = m1 ? m1.negRate : (top1.negRate || 0)
    const oneInN = top1Neg > 0 ? Math.max(1, Math.round(1 / top1Neg)) : 0
    const wordsSample = getTopWords(processed.wordCloud, 3)

    const bestPred = (processed.predictions || [])[0] || null
    const predRow = bestPred
      ? `| ${bestPred.aspect} | ${pct(bestPred.positive_gain)} | ${pct(bestPred.negative_risk)} | ${pct(bestPred.probability)} | **${(parseFloat(bestPred.impactScore || 0)).toFixed(3)}** |`
      : '| 暂无预测数据 | - | - | - | - |'

    const selectedAspectsText = Array.isArray(processed.selectedAspects) && processed.selectedAspects.length
      ? processed.selectedAspects.join('、')
      : '综合表现最需改善的方面'

    const prompt = `你是一名资深产品策略顾问，擅长将枯燥的数据转化为清晰的商业洞察和可执行的战略。现在，请基于提供的口碑数据，为指定车型生成一份具有说服力和行动导向的中文Markdown格式优化报告。\n\n【输入JSON】\n${JSON.stringify(processed)}\n\n【你的核心任务与视角】\n1. 定位：你不是在“汇总数据”，而是在“诊断产品健康状况”并“开具处方”。\n2. 目标：你的核心诉求是 “${processed.roleDescription || '通过精准的口碑优化，提升用户满意度和市场竞争力，最终驱动销量增长'}”。所有分析和建议都必须围绕这一终极目标展开。\n3. 叙事：报告应讲述一个故事：“我们现在在哪？（现状）→ 我们要去哪？（目标）→ 我们该如何前往？（策略）”。\n\n【数据深度解读指南】\n- 解读覆盖度×负面率的组合，明确优先级（高覆盖+高负面=最高优先）。\n- 结合示例文本（aspectInsights[aspect].examples），引用至少2条用户原话（正负面各≥1）以具体化问题。\n- 解释 priorityScore 的来源（覆盖度/负面率/吸纳潜力）与影响。\n\n【输出结构（Markdown）】\n# 车型口碑优化与机遇洞察报告\n\n## 一、诊断背景与战略目标\n- 诊断对象：明确本次分析的车型/车系。\n- 核心诉求：作为${safeCtx.userRoleValue || safeCtx.userRole || '产品经理'}，我们的核心使命是「${processed.roleDescription || '提升用户满意度与销量'}」。本报告旨在为此提供数据驱动的决策支持。\n- 优化焦点：基于策略，我们优先锁定以下口碑维度进行深度优化：${selectedAspectsText}。\n- 数据溯源：分析基于近期的真实用户口碑结构化数据。\n\n## 二、本品口碑健康度总览\n- 总体诊断：用一句话概括本品在市场上的口碑定位（例如：“本品在【优势】上表现良好，但在【劣势】方面面临挑战。”）。\n- 关键指标：\n  - 总体声量：${total} \n  - 平均口碑分：${avgScore} \n  - 用户情绪分布：正面 ${pct(posRatio)} | 中性 ${pct(neuRatio)} | 负面 ${pct(negRatio)} \n- 用户心智图谱：从高频词中提炼2-3个核心感知，并解读其背后含义（例如：${wordsSample || '示例待提炼'}）。\n\n## 三、优化策略优先级矩阵\n以下是我们根据用户影响范围（覆盖度）和问题严重性（负面率）筛选出的优先行动清单。\n\n| 优化方面 | 用户讨论覆盖度 | 负面评价比率 | 吸纳潜力 | 策略优先级 |\n|:--- |---: |---: |---: |---: |\n${matrixRows || '| 暂无数据 | - | - | - | - |'}\n\n**深度洞察：**\n- 对于【${top1.aspect}】：这是当前最突出的矛盾。${top1Neg > 0 ? `约每${oneInN}个提及中就有1个不满` : '负面率极低，保持优势并探索惊喜体验'}，必须优先解决。\n- 竞品吸纳机会：${processed.target === 'competitor' ? `该方面存在约${pct(top1.absorbPotential || 0)}的吸纳空间，可通过产品与传播策略承接优势用户。` : '如需对标竞品，可进一步评估吸纳潜力。'}\n\n## 四、投资回报与风险预测\n对首要优化方面进行投入，预计将产生以下收益与风险：\n\n| 评估方面 | 预期正向收益 | 潜在负向风险 | 成功概率 | 综合影响分 |\n|:--- |---: |---: |---: |---: |\n${predRow}\n\n> 说明：综合影响分是权衡了收益、风险和成功概率后的量化值，分数越高代表该投资越值得优先进行。\n\n## 五、 actionable 行动计划\n1. **首要任务：攻坚【${top1.aspect}】**\n   - 具体措施：${getActionForAspect(top1.aspect)}。\n   - 用户证据：用户明确抱怨“**${getExampleText(top1.aspect)}**”。\n   - 预期影响：预计能直接提升该方面${bestPred ? pct(bestPred.positive_gain) : '若干'}的正向评价。\n   - 风险与缓解：${getRiskForAspect(top1.aspect)}。\n\n2. （后续列出其他高优先级方面的具体计划，包含措施/影响范围/风险缓解/里程碑）。\n\n${processed.optimizationGoal === 'competitor' ? `**【竞品对标策略】**：针对竞品在该方面的优势，应通过技术迭代、供应商优化与营销话术调整进行吸纳和反超。` : ''}\n\n${processed.optimizationGoal === 'ai' ? `**【AI赋能路径】**：引入AI工具自动抓取与归类新增口碑，通过提示工程生成每周优化重点简报，提升分析效率。` : ''}\n\n## 六、总结与后续\n短期内，集中资源解决【${top1.aspect}】${topPool[1] ? `和【${topPool[1].aspect}】` : ''}问题，是扭转口碑态势、实现「${processed.roleDescription || '提升用户满意度与销量'}」目标的最高效路径。建议成立专项小组，按上述计划推进。\n\n注意：仅输出Markdown内容。不要输出JSON或额外解释。`

    return prompt
  }

  // 生成单行第一印象提示词（不超过20字，融入用户角色语气）
  generateOneLineInsightPrompt(context = {}) {
    const safe = { ...(context || {}) }
    const payload = {
      aspect: safe.aspect || '',
      object: safe.object || '',
      mode: safe.mode || '',
      target: safe.target || '',
      wordCloud: (safe.wordCloud || []).map(w => ({ term: w.term, sentiment: w.sentiment })),
      examples: {
        positive: (safe.examples && safe.examples.positive) ? safe.examples.positive.slice(0, 2) : [],
        negative: (safe.examples && safe.examples.negative) ? safe.examples.negative.slice(0, 2) : []
      }
    }
    const roleDescriptions = {
      normal_user: '关注产品使用体验和口碑评价',
      product_manager: '关注产品功能与用户体验',
      marketing: '关注品牌形象与市场反馈',
      data_analyst: '关注数据洞察与趋势分析',
      developer: '关注技术实现与性能优化',
      executive: '关注整体业务与战略决策'
    }
    const roleValue = safe.userRoleValue || 'normal_user'
    const roleLabel = safe.userRole || '普通用户'
    const roleDescription = roleDescriptions[roleValue] || roleDescriptions.normal_user
    return `请以“${roleLabel}”视角（诉求：${roleDescription}）阅读以下JSON，并输出一句中文单行结论（不要换行），概括“${payload.aspect}”的第一印象；只给结论，不要罗列数据或解释；严格限制不超过20个字：\n\n${JSON.stringify(payload)}`
  }

}

export default new AIAnalysisAPI()

